import React from 'react';
import { Zap, AlertCircle, Loader } from 'lucide-react';
import Button from '@components/common/Button';
import DetailSection from '@components/common/DetailSection';
import { Assessment, AssessmentItemsResponse } from '@app-types';
import { LoadingSpinner } from '@components/common/loading';
import styles from './AssessmentFrameworkManager.module.css';

interface AssessmentFrameworkManagerProps {
  assessment: Assessment;
  assessmentItems?: AssessmentItemsResponse;
  canGenerate: boolean;
  onGenerate: () => void;
  isGenerating: boolean;
  children: React.ReactNode;
}

const AssessmentFrameworkManager: React.FC<AssessmentFrameworkManagerProps> = ({
  assessment,
  assessmentItems,
  canGenerate,
  onGenerate,
  isGenerating,
  children
}) => {
  const hasItems = !!assessmentItems?.items?.length;

  // If we have items, render the children (the actual framework)
  if (hasItems) {
    return <>{children}</>;
  }

  // Otherwise, show the generation interface
  return (
    <DetailSection title="Assessment Framework" className={styles.frameworkSection}>
      {/* Loading Overlay */}
      {isGenerating && (
        <div className={styles.frameworkLoadingOverlay}>
          <div className={styles.frameworkLoadingSpinner}>
            <LoadingSpinner size="medium" />
            <p>Generating framework...</p>
          </div>
        </div>
      )}

      {isGenerating ? (
        <div className={styles.frameworkGeneration}>
          <div className={styles.generationStatus}>
            <div className={styles.generationIcon}>
              <Loader size={24} className="spinning" />
            </div>
            <div className={styles.generationContent}>
              <h3>Generating Assessment Framework</h3>
              <p>Creating assessment items based on {assessment.tier_name} requirements...</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.frameworkGeneration}>
          <div className={styles.generationStatus}>
            <div className={styles.generationIcon}>
              <AlertCircle size={24} />
            </div>
            <div className={styles.generationContent}>
              <h3>Assessment Framework Not Generated</h3>
              <p>
                This assessment needs its evaluation framework to be generated based on the supplier's tier (
                {assessment.tier_name}). This will create the specific assessment processes that need to be evaluated.
              </p>

              <div className={styles.tierInfo}>
                <div className={styles.tierDetail}>
                  <strong>Supplier Tier:</strong> {assessment.tier_name}
                </div>
                <div className={styles.tierDetail}>
                  <strong>Risk Level:</strong> {assessment.tier_risk_level}
                </div>
                <div className={styles.tierDetail}>
                  <strong>Contract:</strong> {assessment.contract_number} - {assessment.contract_name}
                </div>
              </div>

              <div className={styles.generationExplanation}>
                <h4>What happens when you generate the framework?</h4>
                <ul>
                  <li>Assessment processes will be loaded based on {assessment.tier_name} requirements</li>
                  <li>Processes will be organized by domain (Risk Management, Contract Governance, etc.)</li>
                  <li>Each process will be ready for evaluation (Implemented, Gap, Not Applicable)</li>
                  <li>Progress tracking will become available</li>
                </ul>
              </div>

              {canGenerate && (
                <div className={styles.generationActions}>
                  <Button variant="primary" onClick={onGenerate} disabled={isGenerating}>
                    <Zap size={16} />
                    Generate Assessment Framework
                  </Button>
                </div>
              )}

              {!canGenerate && (
                <div className={styles.noPermission}>
                  <p>
                    <strong>Note:</strong> You don't have permission to generate the assessment framework. Please
                    contact an administrator or the assigned assessor.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DetailSection>
  );
};

export default AssessmentFrameworkManager;
